import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";

export default function Testimonials() {
  const { t } = useTranslation();
  const ITEMS = useMemo(
    () => [
      {
        quote: t("testimonials.t1.quote"),
        author: "Alessandro Marchetti",
        role: t("testimonials.t1.role"),
        location: "Milano · IT",
      },
      {
        quote: t("testimonials.t2.quote"),
        author: "Camille Moreau",
        role: t("testimonials.t2.role"),
        location: "Paris · FR",
      },
      {
        quote: t("testimonials.t3.quote"),
        author: "Khalid Al-Mansoori",
        role: t("testimonials.t3.role"),
        location: "Dubai · UAE",
      },
      {
        quote: t("testimonials.t4.quote"),
        author: "Olivia Bennett",
        role: t("testimonials.t4.role"),
        location: "Los Angeles · USA",
      },
      {
        quote: t("testimonials.t5.quote"),
        author: t("testimonials.t5.author"),
        role: t("testimonials.t5.role"),
        location: `${t("testimonials.t5.city")} · RU`,
      },
    ],
    [t]
  );
  const [i, setI] = useState(0);
  const ref = useRef<HTMLElement | null>(null);
  const touchX = useRef<number | null>(null);

  useEffect(() => {
    const id = setInterval(() => setI((c) => (c + 1) % ITEMS.length), 8000);
    return () => clearInterval(id);
  }, []);

  const item = ITEMS[i];

  return (
    <section ref={ref} className="bg-[hsl(var(--surface))] text-foreground py-24 lg:py-36 overflow-hidden">
      <div className="lc-container-ext">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-4">
            <span className="inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-foreground/55 before:content-[''] before:inline-block before:h-px before:w-8 before:bg-bronze">
              {t("testimonials.eyebrow")}
            </span>
            <h2 className="mt-6 font-display font-light leading-[1.0] tracking-[-0.035em] text-[40px] md:text-[56px] lg:text-[64px]">
              {t("testimonials.headline_1")}
              <span className="block font-serif italic font-normal text-bronze">
                {t("testimonials.headline_2")}
              </span>
              <span className="block text-foreground/85">{t("testimonials.headline_3")}</span>
            </h2>
          </div>

          <div
            className="lg:col-span-8 relative min-h-[320px] md:min-h-[260px]"
            onTouchStart={(e) => (touchX.current = e.touches[0].clientX)}
            onTouchEnd={(e) => {
              if (touchX.current === null) return;
              const dx = e.changedTouches[0].clientX - touchX.current;
              if (Math.abs(dx) > 40) {
                setI((c) =>
                  dx < 0 ? (c + 1) % ITEMS.length : (c - 1 + ITEMS.length) % ITEMS.length
                );
              }
              touchX.current = null;
            }}
          >
            <Quote className="absolute -top-4 -left-2 h-16 w-16 text-bronze/30" aria-hidden="true" />
            <div key={i} className="lc-caption">
              <p className="font-serif italic text-[24px] md:text-[34px] leading-[1.3] text-foreground/85 max-w-3xl">
                "{item.quote}"
              </p>
              <div className="mt-10 flex items-end justify-between gap-6 flex-wrap">
                <div>
                  <div className="text-foreground text-[15px] font-medium">{item.author}</div>
                  <div className="text-foreground/55 text-[13px] mt-1">
                    {item.role} · {item.location}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    aria-label="Previous"
                    onClick={() => setI((c) => (c - 1 + ITEMS.length) % ITEMS.length)}
                    className="h-11 w-11 rounded-full border border-foreground/20 hover:border-bronze hover:text-bronze transition flex items-center justify-center"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <div className="text-foreground/60 text-[12px] tracking-[0.3em]">
                    {String(i + 1).padStart(2, "0")} / {String(ITEMS.length).padStart(2, "0")}
                  </div>
                  <button
                    type="button"
                    aria-label="Next"
                    onClick={() => setI((c) => (c + 1) % ITEMS.length)}
                    className="h-11 w-11 rounded-full border border-foreground/20 hover:border-bronze hover:text-bronze transition flex items-center justify-center"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="mt-6 flex items-center gap-2">
                {ITEMS.map((_, idx) => (
                  <button
                    key={idx}
                    type="button"
                    aria-label={`Go to testimonial ${idx + 1}`}
                    onClick={() => setI(idx)}
                    className={`h-1.5 rounded-full transition-all ${
                      idx === i ? "w-8 bg-bronze" : "w-4 bg-foreground/20 hover:bg-foreground/40"
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}